const API_BASE = import.meta.env.VITE_API_URL || 'https://oyster-app-rm48i.ondigitalocean.app'

export interface RoundData {
  address: string
  state: number | string
  ticketPrice: string
  prizePool: string
  participantCount: number
  endTime: number
  winners: string[]
  prizes: string[]
}

export interface Winner {
  address: string
  place: 1 | 2 | 3
  prize: string
  roundAddress: string
  timestamp: number
}

// ── Phase 1A: Hybrid Crypto Payout types ─────────────────

export interface AddressValidationResult {
  ok: boolean
  address: string
  txCount: number
  isContract: boolean
  codeSize: number
  suspicious: boolean
  reasons: string[]
}

export interface PayoutValidateResponse extends AddressValidationResult {
  winner: {
    prize: string
    position: number
    roundAddress: string
  }
  attemptsRemaining: number
}

/**
 * Either a new session (a test transfer was sent), or `in_flight`: a payout for
 * this person is already on its way, and no session or test transfer is made.
 */
export interface PayoutInitiateResponse {
  sessionId: string
  testTxHash: string
  expiresAt: number
  status: string
  payoutTxHash?: string
}

/** `pending`: signed and sent, confirmation not seen yet. Never offer a retry. */
export interface PayoutConfirmResponse {
  payoutTxHash: string
  status: 'confirmed' | 'pending' | string
}

/** Longer than the backend's own wait for a receipt, so its answer can arrive. */
const PAYOUT_TIMEOUT_MS = 120_000

export class ApiError extends Error {
  constructor(
    message: string,
    /** 0 when no answer arrived at all. */
    readonly status: number,
    readonly code?: string
  ) {
    super(message)
  }
}

/**
 * No clear answer to a payout call: the money may already be on its way, so the
 * confirm button must not come back. That is no answer at all, a gateway that
 * gave up (502/504), or the backend saying it could not tell. Every other error
 * happened before anything was signed and is safe to try again.
 */
export function isNoAnswer(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  if (err.status === 0 || err.status === 502 || err.status === 504) return true
  // A 503 from our backend always carries a code; a bare one is the load
  // balancer during a deploy, possibly after the container signed and died.
  if (err.status === 503 && !err.code) return true
  return err.code === 'outcome_unknown'
}
export interface WinnerPayout {
  hasPending: boolean
  roundAddress?: string
  winnerAddress?: string
  amount?: string
  position?: number
  recommendedMethod?: 'card' | 'crypto'
  cryptoOnly?: boolean
}
/** GET /api/payout/session — backend services/sessionResume.ts. */
export type ActiveClaimSession =
  | { active: false }
  | {
      active: true
      kind: 'prize' | 'reward'
      step: 'confirm' | 'test_sending'
      sessionId: string
      testTxHash?: string
      destinationAddress: string
      expiresAt: number
    }

export interface ReferrerPayout {
  hasPending: boolean
  amount?: string
  payoutId?: number
  /** How many shares are waiting, and their total (the card shows one at a time). */
  count?: number
  total?: string
}

export interface ReferralInfo {
  code: string
  eligible: boolean
  telegramLink: string
  webLink: string
}

export interface ReferralStats {
  code: string
  eligible: boolean
  telegramLink: string
  webLink: string
  referralCount: number
  activeReferrals: number
  pendingReferrals: number
  branchCount: number
  branchActiveCount: number
}

class LotteryAPI {
  private baseUrl: string

  constructor() {
    this.baseUrl = API_BASE
  }

  private async request<T>(endpoint: string, options?: RequestInit, timeoutMs?: number): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const controller = timeoutMs ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
    let response: Response
    try {
      response = await fetch(url, {
        ...options,
        signal: controller?.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      })
    } catch (err: any) {
      // No answer is not "it failed". For a payout it may well have gone out,
      // and the screen has to say that instead of offering the button again.
      throw new ApiError(
        err?.name === 'AbortError' ? 'The server did not answer in time' : 'Network error',
        0,
        err?.name === 'AbortError' ? 'timeout' : 'network'
      )
    } finally {
      if (timer) clearTimeout(timer)
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      // The backend answers {error: code} and, where a person should read it,
      // {message}. Reading only `message` showed "HTTP 409" for every refusal.
      throw new ApiError(
        body.message || body.error || `HTTP ${response.status}`,
        response.status,
        typeof body.error === 'string' ? body.error : undefined
      )
    }
    return response.json()
  }

  async getCurrentRound(): Promise<RoundData> {
    return this.request<RoundData>('/api/round')
  }

  async getRound(address: string): Promise<RoundData> {
    return this.request<RoundData>(`/api/round/${address}`)
  }

  async buyTickets(data: {
    roundAddress: string
    ticketCount: number
    telegramUserId: number
    starsTransactionId: string
  }): Promise<{ success: boolean; ticketNumbers: number[]; txHash: string }> {
    return this.request('/api/buy', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getUserTickets(roundAddress: string, telegramUserId: number): Promise<number[]> {
    return this.request<number[]>(`/api/tickets/${roundAddress}/${telegramUserId}`)
  }

  async getRecentWinners(limit = 10): Promise<Winner[]> {
    return this.request<Winner[]>(`/api/winners?limit=${limit}`)
  }

  async subscribe(telegramUserId: number): Promise<{ success: boolean }> {
    return this.request('/api/subscribe', {
      method: 'POST',
      body: JSON.stringify({ telegramUserId }),
    })
  }

  async getPendingPayout(telegramUserId: number): Promise<WinnerPayout> {
    return this.authedGet<WinnerPayout>(`/api/payout/pending/${telegramUserId}`)
  }

  // submitCardPayout / submitCryptoPayout removed together with their backend
  // endpoints: those accepted a payout destination with no authentication.
  // Prize claims go through the session flow below (validate → initiate → confirm).

  private getInitData(): string {
    return window.Telegram?.WebApp?.initData ?? ''
  }

  /**
   * Attach the signed Telegram identity when we have one, and say nothing when
   * we do not.
   *
   * Not throwing is deliberate. These calls used to name their user in the URL
   * with nothing to back it up, and the backend is being taught to stop
   * believing that — but it goes through an observe-only stage first, and a
   * client that refused to make the call at all would hide from those logs
   * exactly the stale sessions the stage exists to find. Once enforcement is
   * on, a request with no identity gets a 401, which is the honest answer.
   */
  private authedHeaders(): Record<string, string> {
    const initData = this.getInitData()
    return initData ? { 'x-telegram-init-data': initData } : {}
  }

  private async authedGet<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { headers: this.authedHeaders() })
  }

  private async authedRequest<T>(endpoint: string, body: unknown, timeoutMs?: number): Promise<T> {
    const initData = this.getInitData()
    if (!initData) {
      throw new Error('Telegram initData not available — open this app from Telegram')
    }
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        headers: { 'x-telegram-init-data': initData },
        body: JSON.stringify(body),
      },
      timeoutMs
    )
  }

  async validatePayout(destinationAddress: string): Promise<PayoutValidateResponse> {
    return this.authedRequest<PayoutValidateResponse>('/api/payout/validate', {
      destinationAddress,
    })
  }

  async initiatePayout(destinationAddress: string): Promise<PayoutInitiateResponse> {
    return this.authedRequest<PayoutInitiateResponse>('/api/payout/initiate', {
      destinationAddress,
    }, PAYOUT_TIMEOUT_MS)
  }

  async confirmPayout(sessionId: string): Promise<PayoutConfirmResponse> {
    return this.authedRequest<PayoutConfirmResponse>('/api/payout/confirm', {
      sessionId,
    }, PAYOUT_TIMEOUT_MS)
  }

  async cancelPayout(sessionId: string): Promise<{ status: string }> {
    return this.authedRequest<{ status: string }>('/api/payout/cancel', {
      sessionId,
    })
  }
  /** The person's own open claim, to continue it after the app was closed (Round #3). */
  async getActiveSession(): Promise<ActiveClaimSession> {
    return this.authedGet<ActiveClaimSession>('/api/payout/session')
  }
  async getPendingReferrerPayout(telegramUserId: number): Promise<ReferrerPayout> {
    return this.authedGet<ReferrerPayout>(`/api/payout/referrer/pending/${telegramUserId}`)
  }
  async validateReferrerPayout(destinationAddress: string): Promise<PayoutValidateResponse> {
    return this.authedRequest<PayoutValidateResponse>('/api/payout/referrer/validate', {
      destinationAddress,
    })
  }

  async initiateReferrerPayout(destinationAddress: string): Promise<PayoutInitiateResponse> {
    return this.authedRequest<PayoutInitiateResponse>('/api/payout/referrer/initiate', {
      destinationAddress,
    }, PAYOUT_TIMEOUT_MS)
  }
  async confirmReferrerPayout(sessionId: string): Promise<PayoutConfirmResponse> {
    return this.authedRequest<PayoutConfirmResponse>('/api/payout/referrer/confirm', {
      sessionId,
    }, PAYOUT_TIMEOUT_MS)
  }
  async getReferralCode(telegramUserId: number): Promise<ReferralInfo> {
    return this.authedGet<ReferralInfo>(`/api/referral/code/${telegramUserId}`)
  }

  // The backend takes the id from the signed identity and ignores this field;
  // it is still sent because the bot's copy of this call is what the backend's
  // mismatch check is written against. Note this method currently has no caller
  // in the mini-app — invites arrive through the bot's /start deep link.
  async registerReferral(telegramUserId: number, referralCode: string, roundAddress: string): Promise<{ success: boolean }> {
    return this.request('/api/referral/register', {
      method: 'POST',
      headers: this.authedHeaders(),
      body: JSON.stringify({ telegramUserId, referralCode, roundAddress }),
    })
  }

  async getReferralStats(telegramUserId: number): Promise<ReferralStats> {
    return this.authedGet<ReferralStats>(`/api/referral/stats/${telegramUserId}`)
  }
}

export const api = new LotteryAPI()
